/**
 * Platform-Specific Configuration Form
 * Provides platform-specific configuration options based on selected platform
 */

import React, { useState } from 'react';
import { useConfigStore } from '../stores/configStore';
import type { SystemConfig } from '../types/config';

interface PlatformSpecificConfigProps {
  platform: string;
}

export const PlatformSpecificConfigForm: React.FC<PlatformSpecificConfigProps> = ({ platform }) => {
  const { config, updatePlatform } = useConfigStore();
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (field: string, value: any) => {
    updatePlatform({
      ...config.platform,
      [field]: value,
    });
  };

  const renderEBSConfig = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Oracle E-Business Suite (EBS 12.x) Configuration</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">EBS Instance Name</label>
          <input
            type="text"
            value={config.ebs?.instanceName || ''}
            onChange={(e) => updatePlatform({ ebsInstanceName: e.target.value })}
            placeholder="e.g., PROD, TEST"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Responsibility</label>
          <input
            type="text"
            value={config.ebs?.responsibility || ''}
            onChange={(e) => updatePlatform({ ebsResponsibility: e.target.value })}
            placeholder="e.g., System Administrator"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">AR Module Code</label>
          <input
            type="text"
            value={config.platform?.arModuleCode || ''}
            onChange={(e) => handleChange('arModuleCode', e.target.value)}
            placeholder="e.g., AR"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">AP Module Code</label>
          <input
            type="text"
            value={config.platform?.apModuleCode || ''}
            onChange={(e) => handleChange('apModuleCode', e.target.value)}
            placeholder="e.g., AP"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">PO Module Code</label>
          <input
            type="text"
            value={config.platform?.poModuleCode || ''}
            onChange={(e) => handleChange('poModuleCode', e.target.value)}
            placeholder="e.g., PO"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">GL Module Code</label>
          <input
            type="text"
            value={config.platform?.glModuleCode || ''}
            onChange={(e) => handleChange('glModuleCode', e.target.value)}
            placeholder="e.g., GL"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">EBS-Specific Features</h4>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.ebsUseFlexfields || false}
              onChange={(e) => handleChange('ebsUseFlexfields', e.target.checked)}
              className="mr-2"
            />
            <span>Use Flexfields for IRN/Hash Storage</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.ebsUseCustomTables || false}
              onChange={(e) => handleChange('ebsUseCustomTables', e.target.checked)}
              className="mr-2"
            />
            <span>Use Custom Tables for Extended Data</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.ebsEnableWorkflow || false}
              onChange={(e) => handleChange('ebsEnableWorkflow', e.target.checked)}
              className="mr-2"
            />
            <span>Enable Workflow Integration</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderFusionConfig = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Oracle Fusion Cloud ERP Configuration</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Fusion Instance URL</label>
          <input
            type="url"
            value={config.platform?.fusionInstanceUrl || ''}
            onChange={(e) => handleChange('fusionInstanceUrl', e.target.value)}
            placeholder="https://your-instance.oracle.com"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Fusion Realm</label>
          <input
            type="text"
            value={config.platform?.fusionRealm || ''}
            onChange={(e) => handleChange('fusionRealm', e.target.value)}
            placeholder="e.g., idcs-xxxxx"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">OIC Integration Cloud URL</label>
          <input
            type="url"
            value={config.platform?.oicUrl || ''}
            onChange={(e) => handleChange('oicUrl', e.target.value)}
            placeholder="https://your-oic-instance.oracle.com"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">BIP Report Template</label>
          <input
            type="text"
            value={config.platform?.bipReportTemplate || ''}
            onChange={(e) => handleChange('bipReportTemplate', e.target.value)}
            placeholder="e.g., AR_INVOICE_EXTRACT"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">Fusion-Specific Features</h4>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.fusionUseOIC || false}
              onChange={(e) => handleChange('fusionUseOIC', e.target.checked)}
              className="mr-2"
            />
            <span>Use Oracle Integration Cloud (OIC)</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.fusionUseBIP || false}
              onChange={(e) => handleChange('fusionUseBIP', e.target.checked)}
              className="mr-2"
            />
            <span>Use BI Publisher for Extraction</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.fusionEnableCustomRole || false}
              onChange={(e) => handleChange('fusionEnableCustomRole', e.target.checked)}
              className="mr-2"
            />
            <span>Create Custom Role for AIS Adapter</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderNetSuiteConfig = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Oracle NetSuite Configuration</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">NetSuite Account ID</label>
          <input
            type="text"
            value={config.platform?.netsuiteAccountId || ''}
            onChange={(e) => handleChange('netsuiteAccountId', e.target.value)}
            placeholder="e.g., 1234567"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">SuiteScript Version</label>
          <select
            value={config.platform?.suiteScriptVersion || '2.1'}
            onChange={(e) => handleChange('suiteScriptVersion', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          >
            <option value="2.0">SuiteScript 2.0</option>
            <option value="2.1">SuiteScript 2.1</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">RESTlet Endpoint</label>
          <input
            type="url"
            value={config.platform?.netsuiteRestletEndpoint || ''}
            onChange={(e) => handleChange('netsuiteRestletEndpoint', e.target.value)}
            placeholder="https://your-account.suiteapis.com/services/rest/record/v1"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Custom Record ID (Status)</label>
          <input
            type="text"
            value={config.platform?.netsuiteStatusRecordId || ''}
            onChange={(e) => handleChange('netsuiteStatusRecordId', e.target.value)}
            placeholder="e.g., customrecord_ais_status"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">NetSuite-Specific Features</h4>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.netsuiteUseSuitelet || false}
              onChange={(e) => handleChange('netsuiteUseSuitelet', e.target.checked)}
              className="mr-2"
            />
            <span>Use Suitelet for UI</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.netsuiteUseCustomRecords || false}
              onChange={(e) => handleChange('netsuiteUseCustomRecords', e.target.checked)}
              className="mr-2"
            />
            <span>Use Custom Records for Logs</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.netsuiteEnableScheduledScript || false}
              onChange={(e) => handleChange('netsuiteEnableScheduledScript', e.target.checked)}
              className="mr-2"
            />
            <span>Enable Scheduled Script for Batch Processing</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderJDEConfig = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Oracle JD Edwards (JDE) Configuration</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">JDE Environment</label>
          <input
            type="text"
            value={config.platform?.jdeEnvironment || ''}
            onChange={(e) => handleChange('jdeEnvironment', e.target.value)}
            placeholder="e.g., PD1, PY1"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">JDE Server URL</label>
          <input
            type="url"
            value={config.platform?.jdeServerUrl || ''}
            onChange={(e) => handleChange('jdeServerUrl', e.target.value)}
            placeholder="https://your-jde-server:port"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Orchestrator Studio URL</label>
          <input
            type="url"
            value={config.platform?.orchestratorUrl || ''}
            onChange={(e) => handleChange('orchestratorUrl', e.target.value)}
            placeholder="https://your-orchestrator:port"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">AIS Server URL</label>
          <input
            type="url"
            value={config.platform?.aisServerUrl || ''}
            onChange={(e) => handleChange('aisServerUrl', e.target.value)}
            placeholder="https://your-ais-server"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">JDE-Specific Features</h4>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.jdeUseOrchestrator || false}
              onChange={(e) => handleChange('jdeUseOrchestrator', e.target.checked)}
              className="mr-2"
            />
            <span>Use Orchestrator Studio</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.jdeUseBusinessServices || false}
              onChange={(e) => handleChange('jdeUseBusinessServices', e.target.checked)}
              className="mr-2"
            />
            <span>Use Business Services (BSSV)</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.jdeEnableEnterpriseOnePages || false}
              onChange={(e) => handleChange('jdeEnableEnterpriseOnePages', e.target.checked)}
              className="mr-2"
            />
            <span>Enable Enterprise One Pages for Status UI</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderPeopleSoftConfig = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Oracle PeopleSoft ERP Configuration</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">PeopleSoft Instance URL</label>
          <input
            type="url"
            value={config.platform?.peoplesoftInstanceUrl || ''}
            onChange={(e) => handleChange('peoplesoftInstanceUrl', e.target.value)}
            placeholder="https://your-peoplesoft-instance"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Integration Broker Node</label>
          <input
            type="text"
            value={config.platform?.integrationBrokerNode || ''}
            onChange={(e) => handleChange('integrationBrokerNode', e.target.value)}
            placeholder="e.g., PSFT_INTEGRATION"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Component Interface Name</label>
          <input
            type="text"
            value={config.platform?.componentInterfaceName || ''}
            onChange={(e) => handleChange('componentInterfaceName', e.target.value)}
            placeholder="e.g., CI_AR_INVOICE"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">REST Service Name</label>
          <input
            type="text"
            value={config.platform?.restServiceName || ''}
            onChange={(e) => handleChange('restServiceName', e.target.value)}
            placeholder="e.g., AIS_INVOICE_SERVICE"
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">PeopleSoft-Specific Features</h4>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.peoplesoftUseComponentInterface || false}
              onChange={(e) => handleChange('peoplesoftUseComponentInterface', e.target.checked)}
              className="mr-2"
            />
            <span>Use Component Interface</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.peoplesoftUseIntegrationBroker || false}
              onChange={(e) => handleChange('peoplesoftUseIntegrationBroker', e.target.checked)}
              className="mr-2"
            />
            <span>Use Integration Broker</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.platform?.peoplesoftUseRestService || false}
              onChange={(e) => handleChange('peoplesoftUseRestService', e.target.checked)}
              className="mr-2"
            />
            <span>Use REST Service</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderConfig = () => {
    switch (platform) {
      case 'EBS':
        return renderEBSConfig();
      case 'FUSION':
        return renderFusionConfig();
      case 'NETSUITE':
        return renderNetSuiteConfig();
      case 'JDE':
        return renderJDEConfig();
      case 'PSFT':
        return renderPeopleSoftConfig();
      default:
        return <div>Select a platform to configure</div>;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {errors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
          <h4 className="font-semibold text-red-800 mb-2">Errors:</h4>
          <ul className="list-disc list-inside text-red-700">
            {errors.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      {renderConfig()}
    </div>
  );
};

export default PlatformSpecificConfigForm;
